import React from "react";
import {
  SvgAssignment,
  SvgNotifications,
  SvgDisplaySettings,
  SvgLockOpenRight,
  SvgDiscoverTune,
  SvgMarkdown,
  SvgMarkup,
  SvgAttachMoney,
  SvgPaymentArrowDown,
  SvgCreditCard,
  SvgAccountBalance,
  SvgStore,
  SvgIdCard,
  SvgAccountCircle,
  SvgHandshake,
  SvgVoiceSelection,
  SvgGroups,
  SvgArticle,
  SvgEventList,
  SvgShoppingCart,
  SvgCurrencyExchange,
  SvgRequestQuote,
  SvgLocalActivity,
  SvgChecklist,
  SvgFolderCopy,
  SvgCrown,
  SvgAirportShuttle,
} from "~/components/Icon/icons";

export enum CustomerTab {
  INFORMATION = "informacoes-basicas",
  NOTIFICATIONS = "notificacoes",
  PAYMENTS = "pagamentos",
  CREDENTIALS = "credenciais",
  PROFILES = "profiles",
  BRANCHES = "filiais",
  APPROVAL_MODELS = "modelos-de-aprovacao",
  COSTS_CENTERS = "centros-de-custo",
  EMPLOYEES = "funcionarios",
  POLICIES_EXPENSES = "politicas-expenses",
  ADVANCE_POLICIES = "politicas-de-adiantamento",
  PURCHASING_POLICIES = "politicas-de-compra",
  REASON_TRIPS = "motivos-de-viagem",
  JUSTIFICATIONS = "justificativas",
  EXPENSE_TYPES = "tipo-de-despesa",
  FEES = "taxas",
  MARKUPS = "markups",
  MARKDOWNS = "markdowns",
  PROJECTS = "projetos",
  PHASES = "fases",
  Parameters = "parametros",
  CRM = "CRM",
  BUDGETS = "orcamentos-criados",
  VIEW_BUDGETS = "visualizacao-dos-orcamentos",
  CUSTOMER_EMPLOYEES_GROUPS = "grupos-de-funcionarios",
  BANK_ACCOUNTS = "dados-bancarios",
}

export interface SidebarItemDef {
  title: string;
  id: CustomerTab | string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isPlaceholder?: boolean;
}

export interface SidebarSectionDef {
  title: string;
  key: string;
  items: SidebarItemDef[];
}

export const CUSTOMER_SECTIONS: SidebarSectionDef[] = [
  {
    title: "Visão Geral",
    key: "visao-geral",
    items: [
      { title: "Informações básicas", id: CustomerTab.INFORMATION, icon: SvgAssignment },
      { title: "Notificações", id: CustomerTab.NOTIFICATIONS, icon: SvgNotifications },
      {
        title: "Veículos",
        id: "veiculos-placeholder",
        icon: SvgAirportShuttle,
        isPlaceholder: true,
      },
      { title: "CRM", id: CustomerTab.CRM, icon: SvgDisplaySettings },
    ],
  },
  {
    title: "Configurações da Conta",
    key: "configuracoes-conta",
    items: [
      { title: "Credenciais", id: CustomerTab.CREDENTIALS, icon: SvgLockOpenRight },
      { title: "Parâmetros", id: CustomerTab.Parameters, icon: SvgDiscoverTune },
      { title: "Markdowns", id: CustomerTab.MARKDOWNS, icon: SvgMarkdown },
      { title: "Markups", id: CustomerTab.MARKUPS, icon: SvgMarkup },
      { title: "Taxas", id: CustomerTab.FEES, icon: SvgAttachMoney },
      { title: "Métodos de pagamento", id: CustomerTab.PAYMENTS, icon: SvgPaymentArrowDown },
      {
        title: "Cartões bizpay",
        id: "cartoes-placeholder",
        icon: SvgCreditCard,
        isPlaceholder: true,
      },
      { title: "Dados bancários", id: CustomerTab.BANK_ACCOUNTS, icon: SvgAccountBalance },
    ],
  },
  {
    title: "Configurações da Empresa",
    key: "configuracoes-empresa",
    items: [
      { title: "Filiais", id: CustomerTab.BRANCHES, icon: SvgStore },
      { title: "Perfis", id: CustomerTab.PROFILES, icon: SvgIdCard },
      { title: "Funcionários", id: CustomerTab.EMPLOYEES, icon: SvgAccountCircle },
      { title: "Passageiros VIP", id: "vip-placeholder", icon: SvgCrown, isPlaceholder: true },
      { title: "Centros de custo", id: CustomerTab.COSTS_CENTERS, icon: SvgAccountBalance },
      { title: "Modelos de aprovação", id: CustomerTab.APPROVAL_MODELS, icon: SvgHandshake },
      { title: "Motivos de viagem", id: CustomerTab.REASON_TRIPS, icon: SvgVoiceSelection },
      {
        title: "Grupos de funcionários",
        id: CustomerTab.CUSTOMER_EMPLOYEES_GROUPS,
        icon: SvgGroups,
      },
      { title: "Projetos", id: CustomerTab.PROJECTS, icon: SvgFolderCopy },
      { title: "Orçamentos", id: CustomerTab.BUDGETS, icon: SvgChecklist },
      {
        title: "Bilhetes com créditos",
        id: "bilhetes-placeholder",
        icon: SvgLocalActivity,
        isPlaceholder: true,
      },
    ],
  },
  {
    title: "Políticas",
    key: "politicas",
    items: [
      { title: "Políticas de compra", id: CustomerTab.PURCHASING_POLICIES, icon: SvgShoppingCart },
      {
        title: "Políticas de adiantamento",
        id: CustomerTab.ADVANCE_POLICIES,
        icon: SvgCurrencyExchange,
      },
      { title: "Políticas de despesa", id: CustomerTab.POLICIES_EXPENSES, icon: SvgRequestQuote },
      { title: "Tipo de despesa", id: CustomerTab.EXPENSE_TYPES, icon: SvgEventList },
      { title: "Justificativas", id: CustomerTab.JUSTIFICATIONS, icon: SvgArticle },
    ],
  },
];

export const CUSTOMER_MENU_TABS = CUSTOMER_SECTIONS.flatMap((section) =>
  section.items
    .filter((item) => !item.isPlaceholder)
    .map((item) => ({ title: item.title, id: item.id as CustomerTab })),
);

export const AGENCY_ISSUER_ALLOWED_TABS = [
  CustomerTab.COSTS_CENTERS,
  CustomerTab.PROFILES,
  CustomerTab.EMPLOYEES,
  CustomerTab.APPROVAL_MODELS,
  CustomerTab.BANK_ACCOUNTS,
];

export const getCustomerMenuTabs = (isIssuer = false) => {
  if (isIssuer) {
    return CUSTOMER_MENU_TABS.filter((tab) => AGENCY_ISSUER_ALLOWED_TABS.includes(tab.id));
  }
  return CUSTOMER_MENU_TABS;
};

export const normalizeText = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
